/**
 * SMS-ready estimate formatter.
 *
 * Takes a saved estimate row (the shape returned by GET
 * /api/estimates/:id) and emits a plain-text string the mechanic can
 * paste into iMessage / Android Messages or hand to the customer in
 * person. Phase 2.x — once Twilio is wired (Phase 3+), this same
 * string is the body of the dispatched SMS, so the format already
 * matches that destination.
 *
 * Format choices:
 * - Vehicle line first (year/make/model/VIN-tail) — orients the
 *   customer to which of their cars this is.
 * - Itemized parts + labor with decimal currency (no abbreviations,
 *   no smart quotes — both render reliably across SMS clients).
 * - Subtotal / HST / Total in three lines, total bolded with arrows
 *   (asterisks render as bold in iMessage/Android, plain text
 *   otherwise — safe degradation).
 * - GYM Auto signature at the foot. Shop-aware in case multi-tenant
 *   ever ships customer-facing SMS for non-GYM shops.
 */

import { formatMoney } from '@/lib/format'

export type EstimateForFormat = {
  customer_name: string | null
  vehicle_year: number | null
  vehicle_make: string | null
  vehicle_model: string | null
  vehicle_engine?: string | null
  vehicle_vin: string | null
  work_description: string | null
  parts: {
    primary?: Array<{
      description?: string | null
      vendor?: string | null
      qty?: number | null
      price_each?: number | null
      [key: string]: unknown
    }> | null
  } | null
  labor_hours: number | null
  labor_rate: number | null
  labor_subtotal: number | null
  parts_subtotal: number | null
  subtotal: number | null
  tax: number | null
  total: number | null
  currency: string | null
}

export type FormatOptions = {
  shopName?: string
  shopPhone?: string | null
}

function vehicleLine(e: EstimateForFormat): string | null {
  const ymm = [e.vehicle_year, e.vehicle_make, e.vehicle_model]
    .filter((v) => v !== null && v !== undefined && String(v).length > 0)
    .join(' ')
  if (!ymm) return null
  const tail = e.vehicle_vin ? ` (VIN ${e.vehicle_vin.slice(-6)})` : ''
  return `Vehicle: ${ymm}${tail}`
}

function partsLines(
  e: EstimateForFormat,
  currency: string,
): string[] {
  const items = e.parts?.primary ?? []
  if (items.length === 0) return []
  const out: string[] = ['Parts:']
  for (const p of items) {
    const name = (p.description ?? '').trim() || 'Part'
    const qty = p.qty ?? 1
    const each = p.price_each ?? 0
    const lineTotal = qty * each
    const supplier = p.vendor ? ` [${p.vendor}]` : ''
    out.push(
      `  - ${name}${supplier}: ${qty} x ${formatMoney(each, { currency })} = ${formatMoney(lineTotal, { currency })}`,
    )
  }
  return out
}

function laborLine(e: EstimateForFormat, currency: string): string | null {
  if (e.labor_hours === null || e.labor_rate === null) return null
  const subtotal =
    e.labor_subtotal !== null ? e.labor_subtotal : e.labor_hours * e.labor_rate
  return `Labor: ${e.labor_hours} hr x ${formatMoney(e.labor_rate, { currency })}/hr = ${formatMoney(subtotal, { currency })}`
}

/**
 * Build the SMS text. Returns a single string with `\n` line
 * separators — caller can copy directly to clipboard, the
 * destinations all handle `\n` correctly.
 */
export function formatEstimateSms(
  e: EstimateForFormat,
  opts: FormatOptions = {},
): string {
  const currency = e.currency ?? 'CAD'
  const shopName = opts.shopName ?? 'GYM Auto'
  const greetingTarget = e.customer_name?.trim() || 'there'

  const lines: string[] = []
  lines.push(`Hi ${greetingTarget}, here's your estimate from ${shopName}:`)
  lines.push('')

  const veh = vehicleLine(e)
  if (veh) lines.push(veh)
  if (e.work_description) lines.push(`Work: ${e.work_description}`)
  if (veh || e.work_description) lines.push('')

  lines.push(...partsLines(e, currency))
  const labor = laborLine(e, currency)
  if (labor) lines.push(labor)
  lines.push('')

  if (e.parts_subtotal !== null) {
    lines.push(`Parts subtotal: ${formatMoney(e.parts_subtotal, { currency })}`)
  }
  if (e.labor_subtotal !== null) {
    lines.push(`Labor subtotal: ${formatMoney(e.labor_subtotal, { currency })}`)
  }
  if (e.subtotal !== null) {
    lines.push(`Subtotal: ${formatMoney(e.subtotal, { currency })}`)
  }
  if (e.tax !== null) {
    lines.push(`HST (13%): ${formatMoney(e.tax, { currency })}`)
  }
  if (e.total !== null) {
    lines.push(`*Total: ${formatMoney(e.total, { currency })}*`)
  }

  lines.push('')
  lines.push(`Reply to confirm and we'll book you in.`)
  lines.push(`— ${shopName}${opts.shopPhone ? ` · ${opts.shopPhone}` : ''}`)

  return lines.join('\n')
}
